import { childrenOf, findElement, holdsFields, isContainer, parentOf } from '../format/elements';
import type { ContainerElement, Element, FormDefinition } from '../format/form-definition';

export interface InsertionPosition {
  parentId: string | null;
  index: number;
}

const mayHold = (container: ContainerElement | null, element: Element): boolean => {
  if (container === null) return true;
  if (container.kind === 'section') return element.kind !== 'section';
  return element.kind === 'staticText' || (element.kind === 'field' && !holdsFields(element));
};

export const mayLand = (form: FormDefinition, element: Element, at: InsertionPosition): boolean => {
  const container = at.parentId === null ? null : findElement(form, at.parentId);
  if (container === undefined || (container !== null && !isContainer(container))) return false;
  const elements = container ? childrenOf(container) : form.elements;
  if (!Number.isInteger(at.index) || at.index < 0 || at.index > elements.length) return false;
  if (!mayHold(container, element)) return false;
  for (let around: Element | undefined = container ?? undefined; around; around = parentOf(form, around.id))
    if (around.id === element.id) return false;
  return true;
};

export const landingOf = (form: FormDefinition, id: string): InsertionPosition | undefined => {
  const parent = parentOf(form, id);
  const index = (parent ? childrenOf(parent) : form.elements).findIndex(element => element.id === id);
  return index < 0 ? undefined : { parentId: parent?.id ?? null, index };
};

export const leavesInPlace = (form: FormDefinition, id: string, at: InsertionPosition): boolean => {
  const now = landingOf(form, id);
  return now !== undefined && now.parentId === at.parentId && (at.index === now.index || at.index === now.index + 1);
};

export const landingBeside = (
  form: FormDefinition,
  id: string,
  side: 'before' | 'after',
): InsertionPosition | undefined => {
  const now = landingOf(form, id);
  return now && { parentId: now.parentId, index: side === 'before' ? now.index : now.index + 1 };
};

export const getInsertionPosition = (
  form: FormDefinition,
  element: Element,
  selectedId: string | null,
): InsertionPosition => {
  const atTheEnd: InsertionPosition = { parentId: null, index: form.elements.length };
  const selected = selectedId === null ? undefined : findElement(form, selectedId);
  if (!selected) return atTheEnd;
  if (isContainer(selected) && mayHold(selected, element))
    return { parentId: selected.id, index: childrenOf(selected).length };
  for (let after: Element | undefined = selected; after; after = parentOf(form, after.id)) {
    if (!mayHold(parentOf(form, after.id) ?? null, element)) continue;
    const landing = landingOf(form, after.id);
    if (landing) return { parentId: landing.parentId, index: landing.index + 1 };
  }
  return atTheEnd;
};

export const landingsByStep = (form: FormDefinition, id: string, direction: 'up' | 'down'): InsertionPosition[] => {
  const element = findElement(form, id);
  const now = landingOf(form, id);
  if (!element || !now) return [];
  const landings = landingsInOrder(form);
  const indexOf = (landing: InsertionPosition) =>
    landings.findIndex(other => other.parentId === landing.parentId && other.index === landing.index);
  const before = indexOf(now);
  const after = indexOf({ parentId: now.parentId, index: now.index + 1 });
  const way = direction === 'up' ? landings.slice(0, before).reverse() : landings.slice(after + 1);
  return way.filter(landing => mayLand(form, element, landing));
};

const landingsInOrder = (form: FormDefinition): InsertionPosition[] => {
  const landings: InsertionPosition[] = [];
  const visit = (parentId: string | null, elements: Element[]) => {
    elements.forEach((element, index) => {
      landings.push({ parentId, index });
      if (isContainer(element)) visit(element.id, element.elements);
    });
    landings.push({ parentId, index: elements.length });
  };
  visit(null, form.elements);
  return landings;
};
